import { useMutation } from "@tanstack/react-query";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import useUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUserLayoutPreferences";
import { TABLE_LAYOUT_DEFAULT_FILTERS_KEY_SPLITTER } from "components/common/EntityDataTable/Constants";
import FetchError from "logic/errors/FetchError";
const slicingToJSON = (slicing) => {
  if (slicing === null) {
    return null;
  }
  if (!slicing) {
    return void 0;
  }
  const slicingPreferences = slicing.readOnly !== void 0 ? { read_only: slicing.readOnly } : {};
  return {
    slice_column: slicing.sliceColumn,
    sort_by: slicing.sortBy,
    order: slicing.order,
    ...slicingPreferences
  };
};
const defaultFiltersToJson = (filters) => Object.entries(filters.toJS()).reduce((res, [key, values]) => {
  const mappedValues = values.map((val) => `${key}${TABLE_LAYOUT_DEFAULT_FILTERS_KEY_SPLITTER}${val}`);
  return res.concat(mappedValues);
}, []);
const preferencesToJSON = ({
  attributes,
  sort,
  perPage,
  slicing,
  customPreferences,
  order,
  defaultFilters
}) => ({
  attributes,
  sort: sort ? { order: sort.direction, field: sort.attributeId } : void 0,
  per_page: perPage,
  slicing: slicingToJSON(slicing),
  custom_preferences: customPreferences,
  filters: defaultFilters ? defaultFiltersToJson(defaultFilters) : void 0,
  order
});
const preferencesUrl = (entityTableId, layoutVariant) => {
  const params = layoutVariant ? `?layout_variant=${encodeURIComponent(layoutVariant)}` : "";
  return qualifyUrl(`/entitylists/preferences/${entityTableId}${params}`);
};
const useUpdateUserLayoutPreferences = (entityTableId, layoutVariant) => {
  const { data: userLayoutPreferences = {}, refetch } = useUserLayoutPreferences(entityTableId, layoutVariant);
  const url = preferencesUrl(entityTableId, layoutVariant);
  const mutationFn = (newPreferences) => fetch("POST", url, preferencesToJSON({ ...userLayoutPreferences, ...newPreferences }));
  const resetMutationFn = () => fetch("DELETE", url);
  const { mutateAsync } = useMutation({
    mutationFn,
    onError: (error) => {
      UserNotification.error(`Updating table layout preferences failed with error: ${error}`);
    },
    onSuccess: () => refetch()
  });
  const { mutateAsync: resetAsync } = useMutation({
    mutationFn: resetMutationFn,
    onError: (error) => {
      if (error instanceof FetchError && error.status === 404) return;
      UserNotification.error(`Updating table layout preferences failed with error: ${error}`);
    },
    onSuccess: () => refetch()
  });
  return { mutateAsync, resetAsync };
};
export default useUpdateUserLayoutPreferences;
